import React from 'react';
import { Button, Flex } from '@fluentui/react-northstar';
import i18next from 'i18next';
import { frozenHeaderBottom } from './TypeaheadList';

/**
 * Cancel and Save under a panel that edits several values at once (Shaun, 2026-10-13). Nothing is saved
 * until Save; it lights only when something changed and nothing required is blank. A click away with
 * changes made leaves the panel open - several picks are too much to lose to a stray click - and Escape or
 * Cancel puts everything back.
 */
export const PanelActions = ({ canSave, onSave, onCancel }: { canSave: boolean; onSave: () => void; onCancel: () => void }) => (
  <Flex gap="gap.smaller" hAlign="end">
    <Button size="small" content={i18next.t('common.buttons.cancel')} onClick={onCancel} data-panel-action="cancel" />
    <Button size="small" primary disabled={!canSave} content={i18next.t('common.buttons.save')} onClick={onSave} data-panel-action="save" />
  </Flex>
);

/**
 * Flashes an element's outline twice (.inline-nudge in main.css), in --inline-nudge-color or else the theme's
 * brand colour, to say "this is waiting for you". Restarted if it is already running.
 */
export const nudge = (element: HTMLElement | null | undefined) => {
  if (!element) return;
  element.classList.remove('inline-nudge');
  element.getBoundingClientRect();
  element.classList.add('inline-nudge');
  element.addEventListener('animationend', () => element.classList.remove('inline-nudge'), { once: true });
};

/**
 * Scrolls the boxes around an element to show it, with `width` (a panel hanging off it, wider than the cell)
 * counted in. Up and down it moves just far enough; across, a cell that is not fully in view is centred, so the
 * columns either side of it show too (Shaun, 2026-10-14) - set against the edge it read as cut off.
 * Not scrollIntoView: that also scrolls the app shell's overflow-hidden boxes, which then stay moved (FluentUi
 * CLAUDE.md) - only boxes that scroll are moved.
 */
export const revealInScrollers = (element: HTMLElement | null | undefined, width = 0, margin = 8) => {
  for (let box = element?.parentElement; element && box && box !== document.body; box = box.parentElement) {
    const style = getComputedStyle(box);
    const scrollsY = /(auto|scroll)/.test(style.overflowY) && box.scrollHeight > box.clientHeight;
    const scrollsX = /(auto|scroll)/.test(style.overflowX) && box.scrollWidth > box.clientWidth;
    if (!scrollsY && !scrollsX) continue;
    const target = element.getBoundingClientRect();
    const view = box.getBoundingClientRect();
    if (scrollsY) {
      // The list's frozen headers cover its top edge.
      const top = Math.max(view.top, frozenHeaderBottom(element) ?? view.top);
      if (target.top < top) box.scrollTop -= top - target.top + margin;
      else if (target.bottom > view.bottom) box.scrollTop += target.bottom - view.bottom + margin;
    }
    if (scrollsX) {
      const right = Math.max(target.right, target.left + width);
      if (target.left < view.left || right > view.right) {
        // Centred where it fits; where it does not, its left edge just inside the view.
        const span = right - target.left;
        const offset = span + 2 * margin <= view.width ? (view.width - span) / 2 : margin;
        box.scrollLeft += target.left - view.left - offset;
      }
    }
  }
};

/**
 * A click away from a panel with unsaved changes does nothing to the page: not even focus or the row selection
 * moves, so the panel keeps the keyboard and Escape still reaches it. Measured before this: the click focused
 * and selected the row underneath, and the next Escape went to the grid. The press is stopped here and the
 * click it makes is eaten as it arrives; a press that never becomes a click lets go at the next press.
 *
 * It does point at the panel, so a forgotten one is found (a click on the app's menu just did nothing): its cell
 * is scrolled back into view - the panel follows it - the panel flashes, and Save takes focus, or Cancel while
 * Save is off.
 */
export const holdOutsideClick = (event: MouseEvent, panel?: HTMLElement | null, cell?: HTMLElement | null) => {
  event.preventDefault();
  event.stopPropagation();
  const swallow = (click: Event) => { click.preventDefault(); click.stopPropagation(); };
  window.addEventListener('click', swallow, { capture: true, once: true });
  window.addEventListener('mousedown', () => window.removeEventListener('click', swallow, true), { capture: true, once: true });
  revealInScrollers(cell, panel?.offsetWidth ?? 0);
  nudge(panel);
  const save = panel?.querySelector<HTMLButtonElement>('[data-panel-action="save"]');
  const action = save && !save.disabled ? save : panel?.querySelector<HTMLButtonElement>('[data-panel-action="cancel"]');
  action?.focus({ preventScroll: true });
};

/**
 * Whether an mgt people picker is showing its suggestion list, with people in it - and so has one lit for Tab or
 * Enter to take, as Add Ticket's pickers do (Shaun, 2026-10-13): a list shows on a click or on typing, and again
 * after a pick, with the first person lit. Tabbing into a box shows none - but mgt still marks its flyout open,
 * empty, so the open flag alone would hold Tab in the box; the list's rendered height is what says it shows.
 */
export const pickerListOpen = (picker: HTMLElement | null | undefined) => {
  const root = picker?.shadowRoot;
  if (!(root?.querySelector('mgt-flyout') as { isOpen?: boolean } | null)?.isOpen) return false;
  return (root.querySelector('.flyout-root')?.getBoundingClientRect().height ?? 0) > 0;
};

const actionButtons = (panel: HTMLElement | null) =>
  Array.from(panel?.querySelectorAll<HTMLButtonElement>('[data-panel-action]') ?? [])
    .filter(button => !button.disabled && button.getAttribute('aria-disabled') !== 'true');

/** A key pressed on one of the panel's buttons - Enter there is the button's own, not the panel's Save. */
export const isPanelAction = (target: EventTarget | null) =>
  target instanceof HTMLElement && target.closest('[data-panel-action]') != null;

/** Tab off the panel's fields: forward to the first button, backward (off the first field) to the last. */
export const focusActions = (panel: HTMLElement | null, backward: boolean) => {
  const buttons = actionButtons(panel);
  (backward ? buttons[buttons.length - 1] : buttons[0])?.focus();
};

/**
 * Tab on the panel's buttons keeps focus in the panel: off the last button to the first field, Shift+Tab off
 * the first button to the last field. Between the buttons the browser's own order holds. Returns whether it
 * took the key.
 */
export const wrapActionTab = (event: React.KeyboardEvent, panel: HTMLElement | null, fields: HTMLElement[]) => {
  if (event.key !== 'Tab' || !isPanelAction(event.target)) return false;
  const buttons = actionButtons(panel);
  const at = buttons.indexOf((event.target as HTMLElement).closest('[data-panel-action]') as HTMLButtonElement);
  let target: HTMLElement | null = null;
  if (event.shiftKey) {
    if (at === 0) target = fields[fields.length - 1];
  } else if (at === buttons.length - 1) target = fields[0];
  if (!target) return false;
  event.preventDefault();
  target.focus();
  return true;
};
